import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import ProGate from '../components/ProGate';
import { Trophy, Target, CheckCircle, Circle, Star, Crown, Medal, Flame, Droplets, Apple, Loader, Lock, Sparkles, Award, X } from 'lucide-react';

const ChallengesPreview = () => (
  <div className="challenges-preview">
    <Trophy size={48} />
    <p>Défis & Badges</p>
  </div>
);

const ChallengesPage = () => {
  const { user, profile, getSubscriptionStatus } = useAuth();
  const subscriptionStatus = getSubscriptionStatus();
  const isPro = subscriptionStatus === 'pro' || subscriptionStatus === 'trial';
  
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [userProgress, setUserProgress] = useState({});
  const [userBadges, setUserBadges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [userPoints, setUserPoints] = useState(0);

  const challenges = [
    { id: 'veggies', title: '5 portions de légumes', desc: 'Mangez 5 portions de légumes chaque jour', icon: <Apple size={24} />, color: '#10b981', target: 7, points: 100 },
    { id: 'water', title: 'Hydratation parfaite', desc: 'Buvez 2L d\'eau par jour pendant 7 jours', icon: <Droplets size={24} />, color: '#3b82f6', target: 7, points: 80 },
    { id: 'nosugar', title: 'Zéro sucre ajouté', desc: 'Évitez les sucres ajoutés pendant 5 jours', icon: <Target size={24} />, color: '#f59e0b', target: 5, points: 120 },
    { id: 'protein', title: 'Boost protéines', desc: 'Atteignez votre objectif protéines 6 jours sur 7', icon: <Flame size={24} />, color: '#ef4444', target: 6, points: 90 }
  ];

  const allBadges = [
    { id: 'first_challenge', name: 'Premier défi', icon: '🎯', desc: 'Complétez votre premier défi' },
    { id: 'week_streak', name: 'Semaine parfaite', icon: '🔥', desc: '7 jours consécutifs' },
    { id: 'hydration_master', name: 'Maître hydratation', icon: '💧', desc: 'Complétez 3 défis hydratation' },
    { id: 'veggie_lover', name: 'Amateur de légumes', icon: '🥬', desc: 'Complétez 3 défis légumes' },
    { id: 'top_10', name: 'Top 10', icon: '🏆', desc: 'Entrez dans le top 10' },
    { id: 'champion', name: 'Champion', icon: '👑', desc: '1ère place du classement' }
  ];

  const loadData = useCallback(async () => {
    if (!user || !isPro) return;
    setIsLoading(true);
    try {
      const { data: challengeData } = await supabase.from('user_challenges').select('*').eq('user_id', user.id).eq('status', 'active').single();
      if (challengeData) { setActiveChallenge(challengeData); setUserProgress(challengeData.progress || {}); }

      const { data: badgesData } = await supabase.from('user_badges').select('badge_id').eq('user_id', user.id);
      if (badgesData) setUserBadges(badgesData.map(b => b.badge_id));

      const { data: leaderData } = await supabase.from('challenge_leaderboard').select('user_id, points, profiles(name)').order('points', { ascending: false }).limit(10);
      if (leaderData) setLeaderboard(leaderData);

      const { data: pointsData } = await supabase.from('challenge_leaderboard').select('points').eq('user_id', user.id).single();
      if (pointsData) setUserPoints(pointsData.points);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, isPro]);

  useEffect(() => { if (isPro) loadData(); else setIsLoading(false); }, [loadData, isPro]);

  const joinChallenge = async (challengeId) => {
    const challenge = challenges.find(c => c.id === challengeId);
    try {
      const { data } = await supabase.from('user_challenges').insert({
        user_id: user.id,
        challenge_id: challengeId,
        status: 'active',
        progress: { completed_days: 0, daily_log: [] },
        target: challenge.target
      }).select().single();
      if (data) { setActiveChallenge(data); setUserProgress(data.progress); setShowJoinModal(false); }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const markDayComplete = async () => {
    if (!activeChallenge) return;
    const today = new Date().toISOString().split('T')[0];
    const dailyLog = userProgress.daily_log || [];
    if (dailyLog.includes(today)) return;

    const newProgress = { completed_days: (userProgress.completed_days || 0) + 1, daily_log: [...dailyLog, today] };
    const challenge = challenges.find(c => c.id === activeChallenge.challenge_id);
    const isComplete = newProgress.completed_days >= challenge.target;

    try {
      await supabase.from('user_challenges').update({ 
        progress: newProgress, 
        status: isComplete ? 'completed' : 'active',
        completed_at: isComplete ? new Date().toISOString() : null
      }).eq('id', activeChallenge.id);

      if (isComplete) {
        // Ajouter points
        const { data: existing } = await supabase.from('challenge_leaderboard').select('points').eq('user_id', user.id).single();
        if (existing) {
          await supabase.from('challenge_leaderboard').update({ points: existing.points + challenge.points }).eq('user_id', user.id);
        } else {
          await supabase.from('challenge_leaderboard').insert({ user_id: user.id, points: challenge.points });
        }
        
        // Badge premier défi
        if (!userBadges.includes('first_challenge')) {
          await supabase.from('user_badges').insert({ user_id: user.id, badge_id: 'first_challenge' });
        }
      }

      setUserProgress(newProgress);
      if (isComplete) setActiveChallenge(null);
      loadData();
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const completedToday = userProgress.daily_log?.includes(today);
  const currentChallenge = activeChallenge ? challenges.find(c => c.id === activeChallenge.challenge_id) : null;
  const progressPercent = currentChallenge ? (userProgress.completed_days / currentChallenge.target) * 100 : 0;
  const userRank = leaderboard.findIndex(l => l.user_id === user?.id) + 1;

  return (
    <ProGate
      feature="Défis & Communauté"
      description="Seul, c'est difficile. Ensemble, c'est possible. Rejoignez des centaines de personnes qui relèvent les mêmes défis que vous chaque semaine. Gagnez des badges, grimpez dans le classement, et transformez votre nutrition en jeu. La motivation que vous méritez."
      benefits={[
        "Défis hebdomadaires stimulants",
        "Badges et récompenses motivants",
        "Classement communautaire",
        "La force du groupe pour tenir"
      ]}
    >
      <div className="challenges-page">
        <div className="page-header">
          <div>
            <h1>🏆 Défis de la semaine</h1>
            <p>Relevez des challenges et gagnez des badges !</p>
          </div>
        </div>

        {isLoading ? (
          <div className="loading-state"><Loader size={32} className="spin" /></div>
        ) : (
          <>
            <div className="stats-grid-3">
              <div className="stat-card yellow"><Star size={24} /><div className="stat-content"><span className="stat-value">{userPoints}</span><span className="stat-label">Points</span></div></div>
              <div className="stat-card blue"><Medal size={24} /><div className="stat-content"><span className="stat-value">{userRank || '-'}</span><span className="stat-label">Classement</span></div></div>
              <div className="stat-card purple"><Award size={24} /><div className="stat-content"><span className="stat-value">{userBadges.length}</span><span className="stat-label">Badges</span></div></div>
            </div>

            {currentChallenge ? (
              <div className="active-challenge" style={{ '--challenge-color': currentChallenge.color }}>
                <div className="challenge-top">
                  <div className="challenge-icon" style={{ background: currentChallenge.color }}>{currentChallenge.icon}</div>
                  <div className="challenge-info">
                    <h2>{currentChallenge.title}</h2>
                    <p>{currentChallenge.desc}</p>
                  </div>
                  <div className="challenge-pts"><span>{currentChallenge.points}</span>pts</div>
                </div>
                <div className="challenge-progress">
                  <div className="progress-info"><span>{userProgress.completed_days || 0} / {currentChallenge.target} jours</span><span>{Math.round(progressPercent)}%</span></div>
                  <div className="progress-bar"><div style={{ width: `${progressPercent}%`, background: currentChallenge.color }} /></div>
                </div>
                <div className="days-dots">
                  {[...Array(currentChallenge.target)].map((_, i) => (
                    <div key={i} className={`day-dot ${i < (userProgress.completed_days || 0) ? 'done' : ''}`}>
                      {i < (userProgress.completed_days || 0) ? <CheckCircle size={24} /> : <Circle size={24} />}
                      <span>J{i + 1}</span>
                    </div>
                  ))}
                </div>
                <button className={`validate-btn ${completedToday ? 'done' : ''}`} onClick={markDayComplete} disabled={completedToday}>
                  {completedToday ? <><CheckCircle size={20} /> Validé aujourd'hui !</> : <><Target size={20} /> Valider ma journée</>}
                </button>
              </div>
            ) : (
              <div className="no-challenge">
                <Trophy size={48} />
                <h2>Prêt pour un nouveau défi ?</h2>
                <p>Choisis un challenge et commence à gagner des points !</p>
                <button className="primary-btn" onClick={() => setShowJoinModal(true)}><Sparkles size={20} /> Choisir un défi</button>
              </div>
            )}

            <div className="card">
              <h2><Crown size={20} /> Classement</h2>
              {leaderboard.length > 0 ? (
                <div className="leaderboard">
                  {leaderboard.map((entry, i) => (
                    <div key={entry.user_id} className={`leader-row ${entry.user_id === user?.id ? 'me' : ''}`}>
                      <span className="rank">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}</span>
                      <span className="name">{entry.profiles?.name || 'Anonyme'} {entry.user_id === user?.id && <span className="you-tag">Toi</span>}</span>
                      <span className="points">{entry.points} pts</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-text">Sois le premier à rejoindre un défi !</p>
              )}
            </div>

            <div className="card">
              <h2><Award size={20} /> Mes badges</h2>
              <div className="badges-grid">
                {allBadges.map(badge => {
                  const unlocked = userBadges.includes(badge.id);
                  return (
                    <div key={badge.id} className={`badge-item ${unlocked ? 'unlocked' : 'locked'}`}>
                      <div className="badge-icon">{unlocked ? badge.icon : <Lock size={20} />}</div>
                      <div className="badge-info"><span className="badge-name">{badge.name}</span><span className="badge-desc">{badge.desc}</span></div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {showJoinModal && (
          <div className="modal-overlay" onClick={() => setShowJoinModal(false)}>
            <div className="modal-box" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>🏆 Choisissez votre défi</h2>
                <button onClick={() => setShowJoinModal(false)} className="modal-close-btn"><X size={24} /></button>
              </div>
              <div className="modal-body">
                <div className="challenges-list">
                  {challenges.map(c => (
                    <div key={c.id} className="challenge-option">
                      <div className="ch-icon" style={{ background: c.color }}>{c.icon}</div>
                      <div className="ch-info">
                        <h4>{c.title}</h4>
                        <p>{c.desc}</p>
                        <span className="ch-meta">{c.target} jours • {c.points} points</span>
                      </div>
                      <button className="join-btn" onClick={() => joinChallenge(c.id)}>Relever</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ProGate>
  );
};

export default ChallengesPage;
